import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Search, Activity, Github } from "lucide-react";

import { CommandPalette } from "./CommandPalette";
import { ActivityFeed } from "./ActivityFeed";

export function Layout({ children }: { children: ReactNode }) {
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
      if (e.key === "Escape") setCmdOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-full bg-zinc-950 text-zinc-100">
      {/* Sidebar */}
      <aside className="hidden md:flex w-56 flex-col border-r border-zinc-800/80 bg-zinc-900/40">
        <div className="px-4 py-4 border-b border-zinc-800/80">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="/amzel-logo.svg"
              alt="AMZEL"
              className="h-7 w-7 shrink-0 brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
            />
            <div>
              <div className="font-mono text-sm font-semibold tracking-tight">
                amzel<span className="text-zinc-500">/</span>nest
              </div>
              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">
                EU DMR Knowledge Base
              </div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 text-sm">
          <SidebarLink to="/" label="EU Overview" />
          <SidebarLink to="/sites/PAD2" label="Site Detail (PAD2)" muted />
          <div className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-widest text-zinc-600">
            Quick Filters
          </div>
          <SidebarBadge label="Frequencies expiring &lt; 30d" count={3} tone="err" />
          <SidebarBadge label="Codeplug drift" count={12} tone="warn" />
          <SidebarBadge label="Survey overdue" count={47} tone="warn" />
          <SidebarBadge label="Repeater offline" count={1} tone="err" />
          <div className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-widest text-zinc-600">
            Coming Soon
          </div>
          <SidebarTeaser label="R7 Provisioning" />
          <SidebarTeaser label="Audit Pack Export" />
          <SidebarTeaser label="Spotify-Wrapped Site" />
          <SidebarTeaser label="Slack /amzel bot" />
        </nav>
        <div className="px-3 py-3 border-t border-zinc-800/80 text-[10px] text-zinc-500 font-mono space-y-2">
          <div className="flex items-center gap-2 opacity-80">
            <span className="shrink-0">inspired by</span>
            <a
              href="https://spreenauten.com"
              target="_blank"
              rel="noreferrer"
              title="Spreenauten GmbH"
              className="flex-1 min-w-0 hover:opacity-100 transition-opacity"
            >
              <img
                src="/spreenauten-logo-long.svg"
                alt="Spreenauten"
                className="h-3 w-auto brightness-0 invert opacity-70 hover:opacity-100"
              />
            </a>
          </div>
          <div>v0.1.0 · build {new Date().toISOString().slice(0, 10)}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between px-4 sm:px-6 h-12 border-b border-zinc-800/80 bg-zinc-900/30 backdrop-blur">
          <button
            type="button"
            onClick={() => setCmdOpen(true)}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 hover:border-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Find site, command, asset…</span>
            <kbd className="ml-3 text-[10px] font-mono text-zinc-600 border border-zinc-800 rounded px-1.5 py-0.5">
              ⌘K
            </kbd>
          </button>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <span className="hidden sm:flex items-center gap-1.5 font-mono">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
              <span>api healthy</span>
            </span>
            <button
              type="button"
              className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded hover:bg-zinc-800 font-mono text-zinc-500"
              aria-label="Activity"
            >
              <Activity className="h-3.5 w-3.5" />
              Activity
            </button>
            <a
              href="https://github.com/spreenauten/amzel"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded hover:bg-zinc-800 text-zinc-500"
            >
              <Github className="h-3.5 w-3.5" />
            </a>
          </div>
        </header>

        <main className="flex-1 flex min-h-0">
          <div className="flex-1 overflow-auto">{children}</div>
          <ActivityFeed />
        </main>
      </div>

      {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} />}
    </div>
  );
}

function SidebarLink({ to, label, muted }: { to: string; label: string; muted?: boolean }) {
  return (
    <Link
      to={to}
      className={`block px-2 py-1.5 rounded text-sm ${
        muted ? "text-zinc-400 hover:text-zinc-100" : "text-zinc-200"
      } hover:bg-zinc-800/60`}
    >
      {label}
    </Link>
  );
}

function SidebarBadge({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "err" | "warn";
}) {
  const dot = tone === "err" ? "bg-red-500" : "bg-amber-400";
  return (
    <div className="flex items-center justify-between px-2 py-1 rounded hover:bg-zinc-800/60 text-xs text-zinc-400">
      <span className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <span dangerouslySetInnerHTML={{ __html: label }} />
      </span>
      <span className="font-mono text-zinc-500">{count}</span>
    </div>
  );
}

function SidebarTeaser({ label }: { label: string }) {
  return (
    <div className="px-2 py-1 text-xs text-zinc-500 italic opacity-60 select-none">{label}</div>
  );
}
